import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';

const LoginAs = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const searchParams = new URLSearchParams(window.location.search);
  const tokenHash = searchParams?.get('token_hash');
  const otpType = searchParams?.get('type') || 'magiclink';

  const handleContinue = async () => {
    if (!tokenHash) return;
    setLoading(true);
    setError('');
    try {
      // Clear any existing session on this browser so the target user's session fully replaces it
      try { await supabase?.auth?.signOut({ scope: 'local' }); } catch (_) {}
      const { data, error: verifyError } = await supabase?.auth?.verifyOtp({
        token_hash: tokenHash,
        type: otpType,
      });
      window.history?.replaceState({}, document.title, window.location?.pathname);
      if (verifyError || !data?.session) {
        setError(verifyError?.message || 'This login link is invalid or has expired. Please generate a new one.');
        setLoading(false);
        return;
      }
      window.location?.replace('/dashboard');
    } catch (err) {
      setError(err?.message || 'Unable to sign in with this link.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="bg-card rounded-2xl shadow-elevation-3 w-full max-w-md p-8 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <Icon name="LogIn" size={20} color="#2563eb" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Admin Login As User</h1>
        </div>

        {!tokenHash ? (
          <>
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <Icon name="AlertCircle" size={16} color="#dc2626" className="mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">This login link is missing its token. Please generate a new link from Staff Admin Hub.</p>
            </div>
            <Link to="/login" className="block text-center text-sm text-blue-600 hover:underline">Go to login</Link>
          </>
        ) : (
          <>
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <Icon name="AlertTriangle" size={15} color="#d97706" className="mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700">
                Continuing will sign this browser in as the selected user. Use an incognito / private window to keep your own admin session. This link works once.
              </p>
            </div>
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <Icon name="AlertCircle" size={16} color="#dc2626" className="mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
            <button
              type="button"
              onClick={handleContinue}
              disabled={loading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {loading ? 'Signing in...' : 'Continue as User'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default LoginAs;
